import React, { memo, useCallback, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
	Input,
	InputType,
	Select,
	SliderCheckbox,
	SpinnerOverlay,
	SubmissionButtons,
} from '@components/common';

import useMutateNetworkElementsGetVesPathCu from '@hooks/networkElements/useMutateNetworkElementsGetVesPathCu';
import useMutateNetworkElementsGetVesPathDu from '@hooks/networkElements/useMutateNetworkElementsGetVesPathDu';
import useQueryNetworkElementCheckVesCu from '@hooks/networkElements/useQueryNetworkElementCheckVesCu';
import useQueryNetworkElementCheckVesDu from '@hooks/networkElements/useQueryNetworkElementCheckVesDu';
import useQueryNetworkElementsMiddleware from '@hooks/networkElements/useQueryNetworkElementsMiddleware';
import useQueryNetworkElementsTypes from '@hooks/networkElements/useQueryNetworkElementsTypes';

import { TIdName } from '@interfaces/common/common';
import { INetworkElementCheckIpAddressDTO, INetworkElementDTO } from '@interfaces/networkElements/dto/networkElements';
import { INetworkElement } from '@interfaces/networkElements/networkElements';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { nonNullishMinutesOptions } from '@constants/timeOptions';
import {
	AUTOSWITCH_TIME_INTERVAL_LABEL,
	DEFAULT_SFTP_PATH,
	IP_ADDRESS_TOOLTIP,
	NETCONF_CHECK_PERIOD_LABEL,
	NETWORK_ELEMENT_TYPES,
} from '../constants';
import { ENetworkElementsFields } from './constants';

import { createVesPathGetter } from '../NetworkElementsSFTPForm/utils';
import { networkElementSchema } from './schema';

import styles from './NetworkElementsForm.module.scss';

const MAX_WIDTH = '240px';
const SELECT_MIN_WIDTH = '80px';

export interface INetworkElementsFormProps {
	handleCheckIpAddress: (dto: INetworkElementCheckIpAddressDTO) => void;
	handleCheckName: (dto: TIdName) => void;
	isLoadingAutoSwitchAvailable?: boolean;
	isLoadingForm: boolean;
	networkElement: INetworkElement;
	onClose: () => void;
	setIsDirtyForm: (isDirty: boolean) => void;
	submitHandler: (data: INetworkElementDTO) => void;
	withAutoSwitch?: boolean;
}

const NetworkElementsForm = ({
	handleCheckIpAddress,
	handleCheckName,
	isLoadingAutoSwitchAvailable = false,
	isLoadingForm,
	networkElement,
	onClose,
	setIsDirtyForm,
	submitHandler,
	withAutoSwitch = false,
}: INetworkElementsFormProps) => {
	const schema = networkElementSchema({
		handleCheckIpAddress,
		handleCheckName,
		id: networkElement?.id,
	});

	const {
		control,
		formState: { errors, isDirty },
		handleSubmit,
		register,
		setValue,
		trigger,
		watch,
	} = useForm<FieldValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			directSftp: networkElement?.directSftp,
			netconfPort: networkElement?.netconfPort,
			type: networkElement?.type,
			middlewareId: networkElement?.middlewareId,
			name: networkElement?.name,
			ipAddress: networkElement?.ipAddress,
			userName: networkElement?.userName,
			password: networkElement?.password,
			subscription: networkElement?.subscription,
			autoSwitch: networkElement?.autoSwitch,
			pathCu: networkElement?.pathCu || DEFAULT_SFTP_PATH,
			pathDu: networkElement?.pathDu || DEFAULT_SFTP_PATH,
			lastVesTimePeriod: {
				label: networkElement?.lastVesTimePeriod,
				value: networkElement?.lastVesTimePeriod,
			},
			netconfCheckTimePeriod: {
				label: networkElement?.netconfCheckTimePeriod,
				value: networkElement?.netconfCheckTimePeriod,
			},
		},
	});

	const isAutoSwitchBlocked = (
		!watch(ENetworkElementsFields.subscription)
		&& !watch(ENetworkElementsFields.directSftp)
	);

	const { middlewareOptions, isLoadingMiddlewareOptions } = useQueryNetworkElementsMiddleware();
	const { typeOptions, isLoadingTypeOptions } = useQueryNetworkElementsTypes();

	const { getPathCu } = useMutateNetworkElementsGetVesPathCu();
	const { getPathDu } = useMutateNetworkElementsGetVesPathDu();
	const { isAvailableVesCu, isLoadingCheckVesCu } = useQueryNetworkElementCheckVesCu(networkElement?.id);
	const { isAvailableVesDu, isLoadingCheckVesDu } = useQueryNetworkElementCheckVesDu(networkElement?.id);

	const isSubscribed = watch(ENetworkElementsFields.subscription);
	const isDirectSftp = watch(ENetworkElementsFields.directSftp);
	const isAutoSwitch = watch(ENetworkElementsFields.autoSwitch);
	const networkElementType = watch(ENetworkElementsFields.type)?.value;

	const isCuDuPath = networkElementType === NETWORK_ELEMENT_TYPES.CUDU;
	const isCuPath = isDirectSftp && (networkElementType === NETWORK_ELEMENT_TYPES.CU || isCuDuPath);
	const isDuPath = isDirectSftp && (networkElementType === NETWORK_ELEMENT_TYPES.DU || isCuDuPath);

	useEffect(() => {
		setIsDirtyForm(isDirty);
	}, [isDirty, setIsDirtyForm]);

	useEffect(() => {
		const cuPathValue = networkElementType
			? networkElement?.pathCu || DEFAULT_SFTP_PATH
			: undefined;

		const duPathValue = networkElementType
			? networkElement?.pathDu || DEFAULT_SFTP_PATH
			: undefined;

		setValue(ENetworkElementsFields.pathCu, cuPathValue);
		setValue(ENetworkElementsFields.pathDu, duPathValue);
	}, [
		networkElementType,
		networkElement?.pathCu,
		networkElement?.pathDu,
		setValue,
	]);

	useEffect(() => {
		if (!networkElement?.middlewareId) return;

		const value = middlewareOptions?.find((option) => option?.value === networkElement?.middlewareId) || null;

		setValue(ENetworkElementsFields.middlewareId, value);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		networkElement?.middlewareId,
		middlewareOptions,
		setValue,
	]);

	useEffect(() => {
		if (!networkElement?.type) return;

		const value = typeOptions?.find((option) => option?.value === networkElement?.type.name) || null;

		setValue(ENetworkElementsFields.type, value);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		networkElement?.middlewareId,
		middlewareOptions,
		setValue,
	]);

	const onSubscriptionChange = useCallback((_e: string, checked: boolean) => {
		setValue(ENetworkElementsFields.subscription, checked);

		const isDirectSFTP = watch(ENetworkElementsFields.directSftp);

		if (checked && isDirectSFTP) {
			setValue(ENetworkElementsFields.directSftp, false);
		}

		if (checked && isDirectSFTP) {
			setValue(ENetworkElementsFields.autoSwitch, false);
		}

		if (!checked && !isDirectSFTP) {
			setValue(ENetworkElementsFields.autoSwitch, false);
		}
	}, [setValue, watch]);

	const onDirectSftpChange = useCallback((_e: string, checked: boolean) => {
		setValue(ENetworkElementsFields.directSftp, checked);

		const isSubscription = watch(ENetworkElementsFields.subscription);

		if (checked && isSubscription) {
			setValue(ENetworkElementsFields.subscription, false);
		}

		if (!checked && !isSubscription) {
			setValue(ENetworkElementsFields.autoSwitch, false);
		}
	}, [setValue, watch]);

	const onAutoSwitchChange = useCallback((_e: string, checked: boolean) => {
		setValue(ENetworkElementsFields.autoSwitch, checked);
	}, [setValue]);

	const onGetPathCu = useCallback(() => {
		void createVesPathGetter(getPathCu, setValue, networkElement?.id, 'pathCu', trigger);
	}, [getPathCu, setValue, networkElement?.id, trigger]);

	const onGetPathDu = useCallback(() => {
		void createVesPathGetter(getPathDu, setValue, networkElement?.id, 'pathDu', trigger);
	}, [getPathDu, setValue, networkElement?.id, trigger]);

	const onSubmit = useCallback((values: FieldValues) => {
		const updatedNetworkElementsDTO = {
			id: networkElement?.id,
			netconfPort: values.netconfPort,
			type: values.type?.value,
			middlewareId: values.middlewareId?.value,
			name: values.name,
			ipAddress: values.ipAddress,
			userName: values.userName,
			password: values.password,
			subscription: values.subscription || false,
			directSftp: !!values.directSftp || false,
			pathCu: isCuPath ? values.pathCu : undefined,
			pathDu: isDuPath ? values.pathDu : undefined,
			lastVesTimePeriod: values.lastVesTimePeriod?.value,
			netconfCheckTimePeriod: values.netconfCheckTimePeriod?.value,
			autoSwitch: values.autoSwitch || false,
		};

		submitHandler(updatedNetworkElementsDTO);
	}, [isCuPath, isDuPath, networkElement?.id, submitHandler]);

	const isLoadingVes = isLoadingCheckVesCu || isLoadingCheckVesDu;
	const isLoadingOptions = isLoadingMiddlewareOptions || isLoadingTypeOptions;

	const isLoading = isLoadingForm
		? false
		: networkElement
			? isLoadingVes || isLoadingOptions || isLoadingAutoSwitchAvailable
			: isLoadingOptions;

	return (
		<div className={styles.wrapper}>
			{isLoading && <SpinnerOverlay />}
			<Input
				errorMessage={errors?.name?.message}
				label='Name'
				name={ENetworkElementsFields.name}
				placeholder='Enter name'
				register={register}
				required
				setValue={setValue}
				type={InputType.text}
			/>
			<Input
				errorMessage={errors?.ipAddress?.message}
				label='IP Address'
				name={ENetworkElementsFields.ipAddress}
				placeholder='Enter ip address'
				register={register}
				required
				setValue={setValue}
				tooltipContent={IP_ADDRESS_TOOLTIP}
				type={InputType.text}
			/>
			<Input
				errorMessage={errors?.netconfPort?.message}
				label='Netconf Port'
				name={ENetworkElementsFields.netconfPort}
				placeholder='Enter netconf port'
				register={register}
				setValue={setValue}
				type={InputType.integer}
			/>
			<Select
				control={control}
				errorMessage={errors.middlewareId?.message}
				isError={!!errors?.middleware}
				label='Middleware'
				minWidth={MAX_WIDTH}
				name={ENetworkElementsFields.middlewareId}
				options={middlewareOptions}
				placeholder='Select middleware'
				required
			/>
			<div className={styles.row}>
				<Select
					control={control}
					errorMessage={errors.type?.message}
					isError={!!errors?.type}
					label='Network Element Type'
					minWidth={MAX_WIDTH}
					name={ENetworkElementsFields.type}
					options={typeOptions}
					placeholder='Select type'
					required
				/>
			</div>
			<Input
				errorMessage={errors?.userName?.message}
				label='Username'
				name={ENetworkElementsFields.userName}
				placeholder='Enter username'
				register={register}
				required
				setValue={setValue}
				type={InputType.text}
			/>
			<Input
				containerClassName={styles.passwordInput}
				errorMessage={errors?.password?.message}
				label='Password'
				name={ENetworkElementsFields.password}
				placeholder='Enter password'
				register={register}
				setValue={setValue}
				type={InputType.password}
			/>
			<div className={styles.row}>
				<SliderCheckbox
					checked={isSubscribed}
					className={styles.sliderCheckbox}
					id={ENetworkElementsFields.subscription}
					isLabelAfter
					label='Subscribe to all netconf notifications'
					onChange={onSubscriptionChange}
				/>
			</div>
			<div className={styles.row}>
				<SliderCheckbox
					checked={isDirectSftp}
					className={styles.sliderCheckbox}
					id={ENetworkElementsFields.directSftp}
					isLabelAfter
					label='Direct SFTP PM data retrieval'
					onChange={onDirectSftpChange}
					tooltipText='You will get PM files directly without receiving Netconf Events'
				/>
			</div>
			{withAutoSwitch && !isAutoSwitchBlocked && (
				<div className={styles.row}>
					<SliderCheckbox
						checked={isAutoSwitch}
						className={styles.sliderCheckbox}
						id={ENetworkElementsFields.autoSwitch}
						isLabelAfter
						label='Enable auto switch between channels'
						onChange={onAutoSwitchChange}
						tooltipText='The way of receiving data will be automatically switched after defined time period of inactivity'
					/>
				</div>
			)}
			{isCuPath && (
				<div className={styles.row}>
					<Input
						containerClassName={styles.fullWidth}
						errorMessage={errors?.pathCu?.message}
						label='SFTP Server Path (CU)'
						labelAction={onGetPathCu}
						labelActionDisabled={!isAvailableVesCu}
						labelActionText={networkElement ? 'Get path from VES' : undefined}
						labelActionTooltip='Get path from the recent VES event'
						name='pathCu'
						placeholder='Enter SFTP server path (CU)'
						register={register}
						required
						setValue={setValue}
						type={InputType.text}
					/>
				</div>)
			}
			{isDuPath && (
				<div className={styles.row}>
					<Input
						containerClassName={styles.fullWidth}
						errorMessage={errors?.pathDu?.message}
						label='SFTP Server Path (DU)'
						labelAction={onGetPathDu}
						labelActionDisabled={!isAvailableVesDu}
						labelActionText={networkElement ? 'Get path from VES' : undefined}
						labelActionTooltip='Get path from the recent VES event'
						name='pathDu'
						placeholder='Enter SFTP server path (DU)'
						register={register}
						required
						setValue={setValue}
						type={InputType.text}
					/>
				</div>)
			}
			{isAutoSwitch && (
				<>
					<div className={styles.row}>
						<Select
							className={styles.autoSwitchSelectWrapper}
							control={control}
							errorMessage={errors.lastVesTimePeriod?.message}
							isError={!!errors?.lastVesTimePeriod}
							isOverflowLabelText
							label={AUTOSWITCH_TIME_INTERVAL_LABEL}
							labelClassName={styles.labelClass}
							minWidth={SELECT_MIN_WIDTH}
							name={ENetworkElementsFields.lastVesTimePeriod}
							options={nonNullishMinutesOptions}
							required
							units='min'
						/>
					</div>
					<div className={styles.row}>
						<Select
							className={styles.autoSwitchSelectWrapper}
							control={control}
							errorMessage={errors.netconfCheckTimePeriod?.message}
							isError={!!errors?.netconfCheckTimePeriod}
							isOverflowLabelText
							label={NETCONF_CHECK_PERIOD_LABEL}
							labelClassName={styles.labelClass}
							minWidth={SELECT_MIN_WIDTH}
							name={ENetworkElementsFields.netconfCheckTimePeriod}
							options={nonNullishMinutesOptions}
							required
							units='min'
						/>
					</div>
				</>
			)}
			<SubmissionButtons
				className={styles.buttons}
				negativeHandler={onClose}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Save'
			/>
		</div>
	);
};

export default memo(NetworkElementsForm);
